import type { PortfolioSelection } from "@/dashboard/inventory_management/common/usePortfolioSelection";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

export function useSelectedPortfolioSummary(selection: PortfolioSelection) {
  const { t } = useI18n();

  const selectedPortfolioItems = computed(() => selection.selectedItems);
  const totalSelectedMwh = computed(() => selection.totalSelectedMwh);

  const selectedTechnologies = computed(() => {
    if (selectedPortfolioItems.value.length === 0) return null;
    const techs = new Set(
      selectedPortfolioItems.value
        .map((item) => item.technology_type)
        .filter(Boolean)
    );
    return [...techs].join(", ");
  });

  const selectedProductionMonths = computed(() => {
    if (selectedPortfolioItems.value.length === 0) return null;
    const months = selectedPortfolioItems.value
      .map((item) => item.production_month)
      .filter(Boolean);
    if (months.length === 0) return null;
    const formattedMonths = months
      .map((m) => (m ? m.substring(0, 7) : ""))
      .filter(Boolean);
    const uniqueMonths = [...new Set(formattedMonths)].sort();
    if (uniqueMonths.length === 1) return uniqueMonths[0];
    if (uniqueMonths.length > 1)
      return `${uniqueMonths[0]} - ${uniqueMonths[uniqueMonths.length - 1]}`;
    return null;
  });

  const summarySupport = computed(() => {
    if (selectedPortfolioItems.value.length === 0) return "-";
    // Three-state: null (no data from the registry) must not be lumped in with "No"
    const supports = new Set(
      selectedPortfolioItems.value.map((i) => i.production_support)
    );
    if (supports.size > 1) return t("common.mixed");
    const support = supports.values().next().value;
    if (support === true) return t("common.yes");
    if (support === false) return t("common.no");
    return t("common.unknown");
  });

  return {
    selectedPortfolioItems,
    totalSelectedMwh,
    selectedTechnologies,
    selectedProductionMonths,
    summarySupport,
  };
}
